import { confirmSignIn } from "aws-amplify/auth";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import {
  SelectMultifactorAuthenticationForm,
  SelectMultifactorAuthenticationFormData,
  ThemedAnchor,
  ThemedButton,
  VerifyCodeForm,
} from "@/App/components";
import { AuthenticationContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";

type MulitfactorAuthenticationPageStep = "selectMethod" | "verifyTotp" | "verifySms";

export const LoginMultifactorAuthenticationPage: FC = () => {
  const { t: translate } = useTranslation();
  const { setAuthState, logoutUser } = useContext(AuthenticationContext);
  const [step, setStep] = useState<MulitfactorAuthenticationPageStep>(undefined);
  const location = useLocation();
  const navigate = useNavigateClient();

  useEffect(() => {
    if (location.pathname === "/login-totp-mfa") {
      setStep("verifyTotp");
    } else if (location.pathname === "/login-sms-mfa") {
      setStep("verifySms");
    } else if (location.pathname === "/login-select-mfa") {
      setStep("selectMethod");
    }
  }, [location]);

  const onBackToLogin = useCallback(async () => {
    try {
      await logoutUser();
    } catch (e) {
      // nothing to see here :shrug:
    }
    navigate("/login");
  }, [logoutUser]);

  const onVerifyCode = async (fullCode: string) => {
    try {
      await confirmSignIn({
        challengeResponse: fullCode,
      }).then(() => {
        setAuthState("signedIn");
      });
    } catch {
      throw new Error(translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.unableToVerifyCode"));
    }
  };

  const onMfaTypeSelected = useCallback(async (data: SelectMultifactorAuthenticationFormData) => {
    const { multifactorAuthenticationMode } = data;

    // sort of poorly named, but this will cause the next step to be ready for code verification
    await confirmSignIn({
      challengeResponse: multifactorAuthenticationMode === "sms" ? "SMS" : "TOTP",
    });

    if (data.multifactorAuthenticationMode === "sms") {
      navigate("/login-sms-mfa");
    } else {
      navigate("/login-totp-mfa");
    }
  }, []);

  return (
    <Stack spacing="medium-large">
      <Stack>
        {step === "selectMethod" && (
          <Subheading textAlign="center">
            {translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.selectYourVerificationMethod")}
          </Subheading>
        )}
        {step === "verifyTotp" && (
          <Subheading textAlign="center">
            {translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.enterVerificationCode")}
          </Subheading>
        )}
        <Body textAlign="center">
          {step === "selectMethod" && <>{translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.keepYourAccountSafeMessage")}</>}
          {step === "verifyTotp" && <>{translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.enterTheCodeAuthenticatorApp")}</>}
          {step === "verifySms" && <>{translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.enterTheCodeMobile")}</>}
        </Body>
        {step === "selectMethod" && <SelectMultifactorAuthenticationForm onSubmit={onMfaTypeSelected} />}
        {["verifyTotp", "verifySms"].includes(step) && <VerifyCodeForm handleVerifyCode={onVerifyCode} />}

        {/* {step !== "selectMethod" && (
          <ThemedButton appearance="secondary" onClick={onChangeVerificationMethod}>Choose a different verification method</ThemedButton>
        )} */}
      </Stack>
      <Stack>
        <ThemedButton appearance="text" onClick={() => onBackToLogin()}>
          {translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.backToLogin")}
        </ThemedButton>
        <Body textAlign="center">
          {translate("LOGIC_MULTIFACTOR_AUTHENTICATION_PAGE.havingProblems")}<ThemedAnchor href="tel:(888) 832-2779">(888) 832-2779</ThemedAnchor>.
        </Body>
      </Stack>
    </Stack>
  );
};
